import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { accessionDetailsHeader, AccessionSearchResult, ReturnContainerInfo, UpdateContainerRequestInfo, ErrorMsg } from './register-sample-info';
import { GridDetails, GridEvent, GridHeader } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { RegisterSampleStorage } from './register-sample-storage';
import { merge } from 'lodash';
import { FormControl } from '@angular/forms';
import { of, Subscription, forkJoin, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AccessionDetailsService } from './service/accession-details-service';
import * as moment from 'moment';
import { UrlBuilder } from '../../core';
import { ActivityLogger } from '../../activities/activity-logger';
import { SampleDetailsService } from '../registration/service/sample-details-service';
import { AdditionalContainerInfo } from './additional-container/additional-container.component';
import { maxLengthInBytes } from '../../shared/validators';
import { MdlDialogService, IMdlCustomDialogConfiguration } from '@angular-mdl/core';
import { ConfirmDialogRequest, SHIPMENT_CONFIRM_TOKEN, ConfirmDialogComponent } from '../../site-shipping-tool/shared/confirm-dialog/confirm-dialog.component';
import { DeleteDialogComponent } from '../../site-shipping-tool/shared/delete-warning/delete-warning-dialog.component';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'register-samples',
    template: require('./register-samples.component.html'),
    styles: [require('./register-samples.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterSamplesComponent implements OnDestroy {

    public accessionData: AccessionSearchResult;
    public accessionNumber: string;
    public accessionDetails: any;
    public gridDetails: GridDetails;
    public isLoadingCompleted = true;
    public pagePerItems = [4, 8, 12, 16, 20, 40, 80, 100];
    public containerList: ReturnContainerInfo[];
    public formatedContainerList: ReturnContainerInfo[];
    public searchTerm = new FormControl('');
    public searchSubscription: Subscription;
    public saving = false;
    public header: GridHeader[] = [];
    public reasonList: { header: string, otherReason: string, list: any[] };
    public count: number[];
    public showSubmitLoading = false;
    public containerFormControl = new FormControl('', [maxLengthInBytes(14)]);
    public showAdditionalContainer = false;
    public showBarcodeLoading = false;
    public errorText = '';
    public isValidContainerNumber: boolean;
    public collectionStatusList: { key: number, value: string, showReason: boolean }[];
    public alternateSampleList: any;
    public isReadonly = false;
    constructor(
        private storage: RegisterSampleStorage,
        private changeDetectorRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private accessionService: AccessionDetailsService,
        private samplesService: SampleDetailsService,
        private activatedRoute: ActivatedRoute,
        private registerSampleStorage: RegisterSampleStorage,
        private dialogService: MdlDialogService,
        private activityLogger: ActivityLogger
    ) {
        this.accessionNumber = localStorage.getItem('resumeRegistration');
        if (!this.accessionNumber) {
            this.urlBuilder.esa().sampleManagement().registerNewSample().navigate();
        }

        this.header = this.accessionService.getContainersHeader();
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        this.searchSubscription = searchTermFlow.subscribe((term) => {
            this.searchContainer(term);
            this.changeDetectorRef.markForCheck();
        });
        this.alternateSampleList = this.accessionService.alternateSampleList;
        this.isLoadingCompleted = false;
        forkJoin([
            this.storage.getCollectionStatusList(),
            this.storage.getReasonsList(),
            this.storage.getAlternateSampleReason(),
        ]).pipe(map(([statusList, reasonList, alternateSampleList]) => {
            this.collectionStatusList = statusList,
            this.header.forEach(t => {
                if (t.key === 'collectionStatus') {
                    t.dropDownList = statusList;
                }
            });
            this.reasonList = { header: 'notCollectedReason', otherReason: 'notCollectedOtherReason', list: reasonList };
            this.alternateSampleList.dropDownData.listItem = alternateSampleList;
        })).subscribe(() => this.onValidAccessionEntered(this.accessionNumber));
        this.checkContainerCode();
        activityLogger.logActivity('Pending Registration - Action : Resume Registration');
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
        localStorage.removeItem('resumeRegistration');
    }

    public onValidAccessionEntered($event: string) {
        this.isLoadingCompleted = false;
        forkJoin([
            this.storage.getRegisteredVisitDetails($event),
            this.storage.getContainerList($event),
        ]).pipe(map(([accessionData, containers]) => {
            if (accessionData && accessionData.length) {
                let date = moment(accessionData[0].visitCollectionDateTime);
                accessionData[0].visitCollectionDateTime = date.format('DD-MMM-YYYY');
                this.isReadonly = accessionData[0].submittedOn && this.isReadonly;
                this.activatedRoute.params.subscribe(param => {
                    this.isReadonly = param.type === 'view' && accessionData[0].submittedOn;
                    if (param.type === 'view' && !accessionData[0].submittedOn) {
                        this.urlBuilder.esa().sampleManagement().resumeRegistration().navigate();
                    }
                });
                accessionData[0].visitCollectionTime = date.format('HH:mm');
                this.accessionData = { sampleRegistrationAccessionData: accessionData, isError: false, errorMsg: '' };
                this.accessionDetails = { header: accessionDetailsHeader, data: this.accessionData.sampleRegistrationAccessionData[0] };
            }
            this.loadContainers(containers);
        })).subscribe();
    }

    public onGridItemClick($event: GridEvent) {
        if ($event.header.displayText === 'Instructions') {
            if ($event.isChild) {
                this.resetAlternateContainer($event.data);
                return;
            }
            this.activityLogger.logActivity('Registered Samples - Action : Lab Manual');
            window.open(`${$event.data.projectNumber}/${$event.data.investigatorSiteNumber}/documents/lab-manuals/synopsis`);
        }
        if ($event.header.key === 'collectionStatus') {
            this.accessionService.showDeleteWarning(
                this.alternateSampleList as ConfirmDialogRequest,
                false, 'sm-alternate-container-dialog').subscribe((res: any) => {
                    if (!res.isCancelled) {
                        this.onAlternateContainerSelect($event.data, res.selectedOption);
                    }
                });
        }
    }

    public resetAlternateContainer(data: ReturnContainerInfo) {
        if (!data) { return; }
        this.saving = true;
        let requestInfo: UpdateContainerRequestInfo = {
            registeredContainerId: data.registeredContainerId,
            modifiedon: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        this.storage.resetAlternateContainer(requestInfo).subscribe(res => {
            this.saving = false;
            this.isLoadingCompleted = false;
            this.storage.getContainerList(this.accessionData.sampleRegistrationAccessionData[0].accessionNumber)
                .subscribe((list: ReturnContainerInfo[]) => {
                    this.loadContainers(list);
                });
        });
    }

    public showDeleteWarning(data: ConfirmDialogRequest, showDeleteDialog = false) {
        let dialogRequest: ConfirmDialogRequest = { ...data };
        return this.showConfirmDialog(dialogRequest, showDeleteDialog)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }

    public searchContainer(term: string) {
        if (!this.containerList || !this.containerList.length || !term) {
            this.gridDetails = { header: this.header, data: this.formatedContainerList };
        } else {
            term = term.toLowerCase().trim();
            let filteredData = this.containerList.filter(t => t.containerIdentifier.toLowerCase().indexOf(term) !== -1);
            this.gridDetails = { header: this.header, data: filteredData };
        }
        this.changeDetectorRef.markForCheck();
    }

    public onStatusSelect($event: { header: GridHeader, data: ReturnContainerInfo, selectedValue: any }) {
        if (!$event || !$event.selectedValue || this.isReadonly) { return; }
        if (!this.count.find(t => t === $event.data.registeredContainerId)) {
            this.count.push($event.data.registeredContainerId);
        }
        $event.data.collectionStatusId = $event.selectedValue.key;
        if ($event.data.isReturnMandatory) {
            this.formatedContainerList.forEach(t => {
                if (t.registeredContainerId === $event.data.registeredContainerId) {
                    t.showReason = t.disableLink = $event.selectedValue.showReason;
                }
            });
        } 
        if (!$event.data.isReturnMandatory || !$event.selectedValue.showReason) {
            let requestData: UpdateContainerRequestInfo = {
                registeredContainerId: $event.data.registeredContainerId,
                registeredContainerCollectionStatusId: $event.selectedValue.key,
                registeredContainerNotCollectedReasonId: null,
                notCollectedReason: null
            };
            this.updateContainer(requestData);
        }
    }

    public onReasonSelect($event: { data: ReturnContainerInfo, selectedValue: any }) {
        if (!$event || !$event.selectedValue) { return; }
        $event.data.notCollectedReasonId = $event.selectedValue.key;
        if ($event.data.notCollectedReason !== 'Other') {
            let requestData: UpdateContainerRequestInfo = {
                registeredContainerId: $event.data.registeredContainerId,
                registeredContainerCollectionStatusId: $event.data.collectionStatusId,
                registeredContainerNotCollectedReasonId: $event.selectedValue.key
            };
            this.updateContainer(requestData);
        }
    }
    public onAlternateContainerSelect(data: ReturnContainerInfo, selectedReason: any) {
        if (!data || !selectedReason) { return; }
        let collectedStatus = this.collectionStatusList.find(t => !t.showReason);
        let requestData: UpdateContainerRequestInfo = {
            registeredContainerId: data.registeredContainerId,
            registeredContainerCollectionStatusId: collectedStatus.key,
            registeredContainerAlternateReasonId: selectedReason.key,
            alternateContainerDescription: selectedReason.reason
        };
        this.updateContainer(requestData, true);
    }

    public onSubmitClicked($event: ReturnContainerInfo) {
        let requestData: UpdateContainerRequestInfo = {
            registeredContainerId: $event.registeredContainerId,
            registeredContainerCollectionStatusId: $event.collectionStatusId,
            registeredContainerNotCollectedReasonId: $event.notCollectedReasonId,
            notCollectedReason: $event.notCollectedOtherReason
        };
        this.updateContainer(requestData);
    }

    public disableAction() {
        if (!this.count || !this.containerList || this.count.length !== this.containerList.length || this.isReadonly) { return true; }
        let disable = false;
        this.formatedContainerList.forEach(t => {
            if (t.isReturnMandatory && t.collectionStatus === 'Not Available') {
                if (!t.notCollectedReason || (t.notCollectedReason === 'Other' && !t.notCollectedOtherReason)) {
                    disable = true;
                }
            }
        });
        return disable;
    }

    public async addContainer() {
        let value = this.containerFormControl.value;
        if (!value && value.length <= 0) { return; }
        this.errorText  = '';
        let error = this.accessionService.validatePatientId(value, false);
        if (error && error.length) {
            this.errorText = error;
            this.isValidContainerNumber = false;
            return;
        }
        this.isValidContainerNumber = true;
        if (value.length >= 11 && (value.slice(0, 10) !== this.accessionData.sampleRegistrationAccessionData[0].accessionNumber)) {
            this.isValidContainerNumber = false;
        }
        this.errorText = this.accessionService.validateAdditionalBarcode(this.containerList, value);
        if (this.errorText && this.errorText.length) {
            this.isValidContainerNumber = false;
            return;
        }
        let result: AdditionalContainerInfo = await this.accessionService.showAdditionalContainerDialog(value, ErrorMsg.SampleRegisteredForVisitError, this.isValidContainerNumber).toPromise();
        if (result && result.addContainer) {
            this.storage.insertAdditionalContainer(result, this.accessionData.sampleRegistrationAccessionData[0].registeredVisitId)
                .subscribe((res) => {
                    this.containerFormControl.setValue('');
                    this.isValidContainerNumber = true;
                    this.errorText = '';
                    this.isLoadingCompleted = false;
                    this.storage.getContainerList(this.accessionData.sampleRegistrationAccessionData[0].accessionNumber)
                        .subscribe((list: ReturnContainerInfo[]) => {
                            this.loadContainers(list);
                        });
                });
        }
    }

    public openAdditionalContainer() {
        this.showAdditionalContainer = !this.showAdditionalContainer;
    }

    public barcodeEvent(event: any) {
        this.showAdditionalContainer = false;
        this.containerFormControl.setValue(event.barcodeData);
        if (!event.barcodeData) { return; }
        this.addContainer();
    }

    public cancel() {
        this.urlBuilder.esa().sampleManagement().pendingRegistration().navigate();
    }

    public submitRegistration() {
        this.showSubmitLoading = true;
        let request = {
            registeredVisitId: this.accessionData.sampleRegistrationAccessionData[0].registeredVisitId,
            submittedOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        this.accessionService.showDeleteWarning(
            {
                message: `By clicking "Submit" you affirm that the correct collection status has been
                          applied to each sample and that all visit requirements have been met per protocol`,
                title: 'Sample Accuracy Confirmation',
                isSMAlert: true,
                noButtonText: 'Close',
                yesButtonText: 'Confirm',
                reverseButtons: true,
                subHeaderText: 'Confirmation'
            },
            false, 'sm-confirm-dialog').subscribe((res: any) => {
                if (!res.isCancelled) {
                    this.storage.submitSampleRegistration(request).subscribe(() => {
                        this.samplesService.showSubmitAlert = new Observable(ob => ob.next(true));
                        this.urlBuilder.esa().sampleManagement().registeredSamples().navigate();
                    });
                }
                this.showSubmitLoading = false;
            });

    }

    public async delete() {
        let data = this.accessionData.sampleRegistrationAccessionData[0];
        let result = await this.accessionService.deleteRegistrationReasonDialog(data.accessionNumber, true).toPromise();
        if (result.isCancelled) { return; }
        let deleteReason = result.selectedOption.confirmOptions;
        let request = {
            registeredVisitId: data.registeredVisitId,
            deleteReason: deleteReason,
            deletedOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        await this.registerSampleStorage.deleteSampleRegistration(request).subscribe(() => {
            this.urlBuilder.esa().sampleManagement().registeredSamples().navigate();
        });
    }

    private formatContainerList(dataList: ReturnContainerInfo[]) {
        if (!dataList || dataList.length === 0) { return []; }
        let formatedData = dataList.map(t => {
            t.collectionStatus = !t.collectionStatus ? '' : t.collectionStatus;
            t.notCollectedReason = !t.notCollectedReason ? '' : t.notCollectedReason;
            t.showReason = t.collectionStatus === 'Not Available' && t.isReturnMandatory;
            t.showOtherReason = t.notCollectedReason === 'Other' && t.isReturnMandatory;
            t.disableLink = t.isAlternateContainer || this.isReadonly ? true : false;
            if (t.isAlternateContainer) {
                let alternateData: ReturnContainerInfo = {
                    disableLink: true,
                    returnRequirement: 'Alt. Container',
                    containerIdentifier: t.accessionNumber,
                    label1: t.alternateContainerDescription,
                    registeredContainerId: t.registeredContainerId,
                };
                t.children = [alternateData]; 
            }
            if (t.isAdditionalContainer) {
                let container = t.containerIdentifier;
                t.containerIdentifier = t.barcode;
                t.barcode = container;
            }
            return t;
        });
        formatedData = formatedData.sort((a, b) => a.registeredContainerId < b.registeredContainerId ? -1 : 1);
        let unCollectedData = formatedData.filter(t => t.collectionStatus && t.collectionStatus.length);
        this.count = unCollectedData ? unCollectedData.map(t => t.registeredContainerId) : [];
        return formatedData;
    }

    private updateContainer(requestData: UpdateContainerRequestInfo, reloadData = false) {
        this.saving = true;
        requestData.modifiedon = `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`;
        this.storage.updateContainer(requestData).subscribe((result) => {
            this.saving = false;
            if (reloadData) {
                this.storage.getContainerList(this.accessionData.sampleRegistrationAccessionData[0].accessionNumber)
                    .subscribe((list: ReturnContainerInfo[]) => {
                        this.loadContainers(list);
                    }); 
            }
            this.changeDetectorRef.markForCheck();
        });
    }

    private checkContainerCode() {
        this.containerFormControl.valueChanges.subscribe((value: string) => {
            if (this.isValidContainerNumber) {
                this.isValidContainerNumber = false;
            }
        });
    }

    private loadContainers(containers: ReturnContainerInfo[]) {
        this.containerList = this.formatedContainerList = this.formatContainerList(containers);
        this.gridDetails = { header: this.header, data: this.formatedContainerList };
        this.isLoadingCompleted = true;
        this.changeDetectorRef.markForCheck();
    }

    private showConfirmDialog(dialogRequest: ConfirmDialogRequest, showDeleteDialog = false) {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: showDeleteDialog ? DeleteDialogComponent : ConfirmDialogComponent,
            providers: [{
                provide: SHIPMENT_CONFIRM_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: showDeleteDialog ? 'st-report-delete-dialog' : 'st-report-confirmation-dialog',
            styles: { 'border-radius': '30px' },
            clickOutsideToClose: false
        };

        return this.dialogService.showCustomDialog(configDialog);
    }
}
